import { format, isAfter, isValid, parse } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Modal, Platform, View } from 'react-native';
import { TextInputMask } from 'react-native-masked-text';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import Spacer from '../../../components/Spacer';
import DateTimePicker from '@react-native-community/datetimepicker';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import SelectField from '../../../components/form/SelectField';
import { B } from '../../../components/Typography';
import elementIDs from '../../../../elementIDs';
import { useUserContext } from '../../../context/UserContext';

const StyledFormSelectContainer = styled.View<{
  paddingRight?: number;
  widthPercent: number;
}>`
  padding-right: ${p => (p.paddingRight ? p.paddingRight : 0)}px;
  width: ${p => p.widthPercent}%;
  height: 62px;
`;

const StyledRowContainer = styled.View`
  flex-direction: row;
`;

const StyledCoverView = styled.View`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const StyledTouchableWithoutFeedback = styled(TouchableWithoutFeedback)`
  width: 100%;
  height: 100%;
`;

const StyledSpinnerView = styled.View`
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  width: 340px;
  background-color: white;
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;
`;

const StyledButton = styled.TouchableOpacity`
  width: 100%;
  max-width: 340px;
  background-color: white;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

const StyledView = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const FormDateFieldView = styled.View`
  margin-top: 6px;
  width: 100%;
`;

const StyledTextInputView = styled.View<{
  minHeight?: number;
  error?: boolean;
  hasHelperText?: boolean;
}>`
  background-color: #fff;
  height: 56px;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  border-radius: 8px;
  flex-direction: row;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledTextInputMask = styled(TextInputMask)<{ empty?: boolean }>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 16px;
  padding: 16px;
  flex: 1;
`;

const HelperText = styled.Text`
  font-family: 'WorkSans-Regular';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)`
  color: ${Colors.error};
`;

export type FormDateConfig = {
  placeholder?: string;
};

export type FormBirthDateProps<T> = FormElementProps<T, FormDateConfig>;

const FormBirthDate = <T extends object>({
  element
}: FormBirthDateProps<T>) => {
  const key = 'birthDate' as keyof T;
  if (!element?.dataSource) element.dataSource = 'birthDate';
  const { user } = useUserContext({ ignoreUndefined: true });
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    FormDateConfig
  >();
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState((formValues[key] as any) || null);
  const [error, setError] = useState('');
  const previousValueRef = useRef(value);

  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedBirthDateIOS, setSelectedBirthDateIOS] = useState<Date>(
    formValues[key] ? new Date(formValues[key] as any) : new Date()
  );

  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (
      !focused &&
      formValues[key] &&
      previousValueRef.current !== formValues[key]
    ) {
      setValue(formValues[key]);
      previousValueRef.current = formValues[key];
    }
  }, [focused, formValues]);

  const getDate = () => {
    const date = new Date();
    date.setDate(date.getDate());
    const dateKey = format(date, 'yyyy-MM-dd');
    return dateKey;
  };

  const todayMarkStyle = {
    selected: true,
    selectedColor: '#8c538a',
    selectedTextColor: Colors.lightText
  };

  const dateType = 'birth';

  let formattedDateValue = value;
  if (value) {
    const date = parse(value, 'yyyy-MM-dd', new Date());
    if (isValid(date)) {
      formattedDateValue = format(date, 'MM/dd/yyyy');
    }
  }

  const handleValidate = (date: Date) => {
    if (!isValid(date)) {
      setFormValue(key, null as any, element);
      setError('Invalid Date.');
      return false;
    }
    if (dateType === 'birth' && isAfter(date, new Date())) {
      // don't allow future dates
      setFormValue(key, null as any, element);
      setError('Please select a date in the past.');
      return false;
    }
    setError('');
    return true;
  };

  const toValidDate = (text: string) => {
    const date = text.split('/');
    if (date.length === 3) {
      if (Number(date[0]) > 12) {
        date[0] = '12';
      }
      if (
        Number(date[1]) >
        new Date(Number(date[2]), Number(date[0]), 0).getDate()
      ) {
        date[1] = String(
          new Date(Number(date[2]), Number(date[0]), 0).getDate()
        );
      }
      return date.join('/');
    }
    return text;
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const getYears = () => {
    const res: string[] = [];
    const latestYear = new Date().getFullYear();
    for (let i = 0; i < 150; i++) {
      res.push(String(latestYear - i));
    }
    return res;
  };

  const [webBirthDate, setWebBirthDate] = useState<{
    month: string;
    year: string;
    day: string;
  }>(
    formValues[key]
      ? {
          month: monthNames[Number(String(formValues[key]).split('-')[1]) - 1],
          year: String(formValues[key]).split('-')[0],
          day: String(formValues[key]).split('-')[2]
        }
      : {
          month: '',
          year: '',
          day: ''
        }
  );

  const daysInMonth = (month: number, year: number, def?: boolean) => {
    const res: string[] = [];
    if (def) {
      for (let i = 0; i < 31; i++) {
        if (String(i + 1).length === 1) {
          res.push('0' + String(i + 1));
        } else {
          res.push(String(i + 1));
        }
      }
    } else {
      for (let i = 0; i < new Date(year, month + 1, 0).getDate(); i++) {
        if (String(i + 1).length === 1) {
          res.push('0' + String(i + 1));
        } else {
          res.push(String(i + 1));
        }
      }
    }
    return res;
  };

  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      dateType === 'birth' &&
      webBirthDate.month &&
      webBirthDate.year &&
      webBirthDate.day
    ) {
      if (
        Number(webBirthDate.day) >
        daysInMonth(
          monthNames.indexOf(webBirthDate.month),
          Number(webBirthDate.year)
        ).length
      ) {
        setWebBirthDate({
          month: webBirthDate.month,
          year: webBirthDate.year,
          day: String(
            daysInMonth(
              monthNames.indexOf(webBirthDate.month),
              Number(webBirthDate.year)
            ).length
          )
        });
      }
      const date = new Date();
      date.setFullYear(Number(webBirthDate.year));
      date.setMonth(monthNames.indexOf(webBirthDate.month));
      date.setDate(Number(webBirthDate.day));
      if (handleValidate(date)) {
        const birthDate = format(date, 'yyyy-MM-dd');
        if (user) user.birthDate = birthDate;
        setFormValue(key, birthDate as any, element);
      }
    }
  }, [webBirthDate]);

  return (
    <View>
      <FormElementQuestion element={element} />
      <FormDateFieldView testID={elementIDs.dateID}>
        {dateType === 'birth' && Platform.OS === 'web' ? (
          <StyledRowContainer>
            <StyledFormSelectContainer widthPercent={50} paddingRight={8}>
              <SelectField
                placeholder='Month'
                value={webBirthDate.month || ' '}
                options={monthNames}
                onChange={(option: any) => {
                  setWebBirthDate({
                    month: String(option.value),
                    year: webBirthDate.year,
                    day: webBirthDate.day
                  });
                }}
              />
            </StyledFormSelectContainer>
            <StyledFormSelectContainer widthPercent={25} paddingRight={8}>
              <SelectField
                placeholder='Day'
                value={webBirthDate.day || ' '}
                options={
                  webBirthDate.month && webBirthDate.year
                    ? daysInMonth(
                        monthNames.indexOf(webBirthDate.month),
                        Number(webBirthDate.year)
                      )
                    : daysInMonth(0, 0, true)
                }
                onChange={(option: any) => {
                  setWebBirthDate({
                    month: webBirthDate.month,
                    year: webBirthDate.year,
                    day: String(option.value)
                  });
                }}
              />
            </StyledFormSelectContainer>
            <StyledFormSelectContainer widthPercent={25}>
              <SelectField
                placeholder='Year'
                value={webBirthDate.year || ' '}
                options={getYears()}
                onChange={(option: any) => {
                  setWebBirthDate({
                    month: webBirthDate.month,
                    year: String(option.value),
                    day: webBirthDate.day
                  });
                }}
              />
            </StyledFormSelectContainer>
          </StyledRowContainer>
        ) : (
          <StyledTextInputView>
            <StyledTextInputMask
              type='datetime'
              options={{
                format: 'MM/DD/YYYY',
                mask: 'MM/DD/YYYY'
              }}
              blurOnSubmit={false}
              value={formattedDateValue}
              empty={!formattedDateValue}
              onChangeText={text => {
                setValue(toValidDate(text));
                const date = parse(text || '', 'MM/dd/yyyy', new Date());
                if (handleValidate(date)) {
                  const birthDate = format(date, 'yyyy-MM-dd');
                  if (user) user.birthDate = birthDate;
                  setFormValue(key, birthDate as any, element);
                }
              }}
              onFocus={() => {
                setFocused(true);
                // setFocusedInput(textInputMaskRef.current);
              }}
              onBlur={event => {
                setFocused(false);
                // handleBlur(name)(event);
                // setFocusedInput(undefined);
              }}
              // onContentSizeChange={event => {
              //   setHeight(event.nativeEvent.contentSize.height);
              // }}
              onSubmitEditing={() => {
                // focusNext();
                setFocused(false);
                Keyboard.dismiss();
              }}
              placeholder='MM/DD/YYYY'
              placeholderTextColor={Colors.placeholderColor}
            />
            {dateType === 'birth' && Platform.OS !== 'web' && (
              <StyledCoverView>
                <StyledTouchableWithoutFeedback
                  onPress={() => {
                    setShowSpinner(true);
                  }}
                >
                  <View />
                </StyledTouchableWithoutFeedback>
              </StyledCoverView>
            )}
          </StyledTextInputView>
        )}

        {Boolean(error) && <ErrorText>{error}</ErrorText>}
        {/* : (
             helperText && <HelperText>{helperText}</HelperText>
          )} */}
        {dateType === 'birth' && Platform.OS === 'android' && showSpinner && (
          <DateTimePicker
            mode='date'
            value={
              formValues[key] ? new Date(formValues[key] as any) : new Date()
            }
            display='spinner'
            onChange={date => {
              setShowSpinner(false);
              if (
                typeof date.nativeEvent.timestamp !== 'undefined' &&
                date.type === 'set'
              ) {
                if (handleValidate(new Date(date.nativeEvent.timestamp))) {
                  const birthDate = format(
                    new Date(date.nativeEvent.timestamp),
                    'yyyy-MM-dd'
                  );
                  if (user) user.birthDate = birthDate;
                  setFormValue(key, birthDate as any, element);
                }
              }
            }}
          />
        )}
        {dateType === 'birth' && Platform.OS === 'ios' && (
          <Modal visible={showSpinner} transparent={true} animationType='fade'>
            <StyledModalBackgroundContainer
              onPress={() => {
                setShowSpinner(false);
              }}
            >
              <StyledView>
                <StyledModalBackground />
                <StyledSpinnerView>
                  <StyledDateTimePicker
                    onChange={date => {
                      if (typeof date.nativeEvent.timestamp !== 'undefined') {
                        setSelectedBirthDateIOS(
                          new Date(date.nativeEvent.timestamp)
                        );
                      }
                    }}
                    mode='date'
                    value={selectedBirthDateIOS}
                    display='spinner'
                  />
                </StyledSpinnerView>
                <Spacer size={1} />
                <StyledButton
                  testID={elementIDs.iosDateCancelID}
                  onPress={() => {
                    setShowSpinner(false);
                  }}
                >
                  <B color='dark'>Cancel</B>
                </StyledButton>
                <Spacer size={1} />
                <StyledButton
                  testID={elementIDs.iosDateSaveID}
                  onPress={() => {
                    if (selectedBirthDateIOS) {
                      if (handleValidate(selectedBirthDateIOS)) {
                        const birthDate = format(
                          selectedBirthDateIOS,
                          'yyyy-MM-dd'
                        );
                        if (user) user.birthDate = birthDate;
                        setFormValue(key, birthDate as any, element);
                      }
                    }
                    setShowSpinner(false);
                  }}
                >
                  <B>Save</B>
                </StyledButton>
              </StyledView>
            </StyledModalBackgroundContainer>
          </Modal>
        )}
      </FormDateFieldView>
    </View>
  );
};

export const FormBirthDatePlugin: FormElementPlugin<any, FormDateConfig> = {
  name: 'Birth Date',
  type: 'BirthDate',
  element: FormBirthDate
};
